import { isSameDay, isBefore, isAfter } from 'date-fns';
import dayjs from 'dayjs';

import { BODY_HEIGHT, BODY_WEIGHT, VITAL_SIGN_TYPES } from '@constants/fhir';
import { getLabReportMap } from '@constants/option-types/lab';

import type { GenericRecord } from '@models/generic-record';
import type { LabResultObservationRecord } from '@models/lab-result';
import type {
  LegalDirectiveRecord,
  CategoryType,
} from '@models/legal-directive';
import type { VitalSignObservationRecord } from '@models/vital-sign';

export function getRecordsRange(
  records: GenericRecord[] = [],
  minDate?: Date,
  maxDate?: Date
) {
  const recordsRange = records.reduce<
    Record<string, Record<string, GenericRecord[]>>
  >((acc, record) => {
    let year = '';
    let dateString = '';
    if (!record.displayDate) {
      year = '?';
      dateString = 'Unknown Date';
    } else if (new RegExp(/^\d{4}$/u, 'u').test(record.displayDate)) {
      year = record.displayDate;
      dateString = 'Unknown Date';
    } else {
      year = dayjs(record.displayDate).format('YYYY');
      dateString = record.displayDate;
    }
    if (!(year in acc)) {
      acc[year] = {};
    }
    const filteredRecords = records.filter((r: GenericRecord) => {
      const recordDate = r.date;
      if (typeof recordDate === 'string') {
        return recordDate === year;
      } else if (
        recordDate === undefined ||
        typeof record.date === 'string' ||
        record.date === undefined
      ) {
        return false;
      }
      return (
        record.date &&
        recordDate &&
        isSameDay(record.date, recordDate) &&
        (maxDate ? isBefore(record.date, maxDate) : true) &&
        (minDate ? isAfter(record.date, minDate) : true)
      );
    });
    if (filteredRecords.length > 0) {
      if (dateString in acc[year]) {
        acc[year][dateString].concat(filteredRecords);
      } else {
        acc[year][dateString] = filteredRecords;
      }
    }
    return acc;
  }, {});

  const result: Record<string, Record<string, GenericRecord[]>> = {};
  for (const year of Object.keys(recordsRange)) {
    if (Object.keys(recordsRange[year]).length !== 0) {
      result[year] = recordsRange[year];
    }
  }
  return result;
}

export const legalAcceptance = (
  type: CategoryType,
  record?: LegalDirectiveRecord
) => {
  if (!record) {
    return {};
  }
  const acceptsAll = record.consentToAll(type);
  const acceptsNone = record.consentToNone(type);
  const acceptsSome = !record.consentToAll(type) && !record.consentToNone(type);

  return { acceptsAll, acceptsNone, acceptsSome };
};

export const getHeightDisplay = (
  observation?: LabResultObservationRecord | VitalSignObservationRecord
) => {
  if (observation) {
    const { component } = observation;
    if (component && component.length > 1) {
      const heightValues = component.map((a) => a?.valueQuantity);
      const feet = heightValues.find((a) => a?.unit === 'ft')?.value;
      const inches = heightValues.find((a) => a?.unit === 'in')?.value;
      if (typeof feet === 'number' && typeof inches === 'number') {
        return `${feet}' ${inches}"`;
      }
    } else {
      const { unit, value } = observation;
      const { format } = VITAL_SIGN_TYPES[BODY_HEIGHT];
      return `${
        format && typeof value === 'number' ? format(value) : value
      } ${unit}`;
    }
  }
  return null;
};

export const getWeightDisplay = (
  observation?: LabResultObservationRecord | VitalSignObservationRecord
) => {
  if (observation) {
    const { unit, value } = observation;
    const { format } = VITAL_SIGN_TYPES[BODY_WEIGHT];
    return `${
      format && typeof value === 'number' ? format(value) : value
    } ${unit}`;
  }
  return null;
};

export const getBloodTypeDisplay = (
  observation?: LabResultObservationRecord | null
): string | null => {
  const value = observation?.value;
  if (typeof value === 'string') {
    return value;
  }
  return null;
};

/**
 * @param reportCode Lab report's code
 * @param observationCode  Lab observation's code
 * @returns {k: string, v: string}[] - Array of lab observation unit options
 */
export const getUnitOptions = (reportCode: string, observationCode: string) => {
  const reportMap = getLabReportMap(reportCode).units;
  if (!reportMap || !reportMap[observationCode]) return [];
  const units = reportMap[observationCode];
  return units.map((unit) => ({ k: unit, v: unit }));
};

/**
 * @param code Lab report's code
 * @returns string[] - Array of lab observation codes
 */
export const getLabObservationTypes = (
  code: string
): {
  k: string;
  v: string;
}[] => {
  const reportMap = getLabReportMap(code).types;
  if (!reportMap) return [];
  return Object.entries(reportMap)
    .map(([key, value]) => ({
      k: key,
      v: value,
    }))
    .sort((a, b) => {
      if (a.k === 'other') {
        return 1;
      }
      if (b.k === 'other') {
        return -1;
      }
      return a.v.localeCompare(b.v);
    });
};
